import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const blankQuestion = () => ({ key: "", label: "", type: "rating", required: true });

export default function Surveys() {
  const [bench, setBench] = useState(null);
  const [draft, setDraft] = useState({ key: "", name: "", sample_pct: 100,
                                        provider_key: "", questions: [blankQuestion()] });
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    apiFetch("/surveys/workbench").then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setError("Surveys are not available to you"); return; }
      setError(null);
      setBench(body);
    });
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    load();
  }, [navigate, load]);

  const post = (path, body, label, then) =>
    apiFetch(path, { method: "POST", body: JSON.stringify(body) }).then(async (r) => {
      const out = await r.json();
      if (!r.ok) {
        setMessage(`${label} failed — ${typeof out.detail === "string" ? out.detail
                                                                       : JSON.stringify(out.detail)}`);
        return;
      }
      setMessage(`${label} done`);
      if (then) then(out);
      load();
    });

  const setQuestion = (i, patch) =>
    setDraft((d) => ({ ...d,
                       questions: d.questions.map((q, j) => (i === j ? { ...q, ...patch } : q)) }));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="surveys-title">Survey Builder</h1>
          <p className="text-sm text-muted-foreground">
            Write your own questions, preview them, publish them.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="surveys-admin-link">
            <Link to="/admin">Admin Center</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="surveys-error">{error}</p>}
        {message && <p className="text-sm text-amber-800" data-testid="surveys-message">{message}</p>}

        {bench && (
          <>
            <p className="text-sm text-muted-foreground" data-testid="surveys-method">
              {bench.method}
            </p>
            <p className="text-sm" data-testid="surveys-publish-note">{bench.publish_note}</p>

            {bench.can_author && (
              <section className="space-y-3" data-testid="survey-builder">
                <h2 className="text-base md:text-lg font-medium">Build a survey</h2>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <label htmlFor="survey-key" className="text-sm font-medium">
                      Key (its permanent name)
                    </label>
                    <Input id="survey-key" data-testid="survey-key" value={draft.key}
                           placeholder="clinic-satisfaction"
                           onChange={(e) => setDraft({ ...draft, key: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="survey-name" className="text-sm font-medium">Title</label>
                    <Input id="survey-name" data-testid="survey-name" value={draft.name}
                           placeholder="How was your IT request handled?"
                           onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="survey-sample" className="text-sm font-medium">
                      Ask this share of people (%)
                    </label>
                    <Input id="survey-sample" type="number" min="0" max="100"
                           data-testid="survey-sample" value={draft.sample_pct}
                           onChange={(e) => setDraft({ ...draft,
                                                       sample_pct: Number(e.target.value) })} />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="survey-provider" className="text-sm font-medium">
                      Provider it belongs to
                    </label>
                    <Input id="survey-provider" data-testid="survey-provider"
                           value={draft.provider_key} placeholder="it"
                           onChange={(e) => setDraft({ ...draft,
                                                       provider_key: e.target.value })} />
                  </div>
                </div>

                <div className="space-y-3" data-testid="survey-questions">
                  <h3 className="text-sm font-semibold">Questions</h3>
                  {draft.questions.map((q, i) => (
                    <div key={i} className="border rounded-md p-3 grid gap-2 md:grid-cols-4"
                         data-testid={`survey-question-${i}`}>
                      <div className="space-y-1">
                        <label htmlFor={`q-key-${i}`} className="text-xs">Key</label>
                        <Input id={`q-key-${i}`} data-testid={`survey-question-key-${i}`}
                               value={q.key}
                               onChange={(e) => setQuestion(i, { key: e.target.value })} />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <label htmlFor={`q-label-${i}`} className="text-xs">
                          What are you asking?
                        </label>
                        <Input id={`q-label-${i}`} data-testid={`survey-question-label-${i}`}
                               value={q.label}
                               onChange={(e) => setQuestion(i, { label: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`q-type-${i}`} className="text-xs">Answer</label>
                        <select id={`q-type-${i}`} data-testid={`survey-question-type-${i}`}
                                className="w-full border rounded-md px-2 py-2 text-sm bg-background"
                                value={q.type}
                                onChange={(e) => setQuestion(i, { type: e.target.value })}>
                          {bench.question_types.map((t) => (
                            <option key={t} value={t}>
                              {t === "rating" ? "rating 1–5" : "free text"}
                            </option>
                          ))}
                        </select>
                        <div className="flex items-center gap-2">
                          <input id={`q-req-${i}`} type="checkbox" checked={q.required}
                                 data-testid={`survey-question-required-${i}`}
                                 onChange={(e) => setQuestion(i, { required: e.target.checked })} />
                          <label htmlFor={`q-req-${i}`} className="text-xs">Required</label>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid="survey-add-question"
                            onClick={() => setDraft({ ...draft,
                                                      questions: [...draft.questions,
                                                                  blankQuestion()] })}>
                      Add a question
                    </Button>
                    {draft.questions.length > 1 && (
                      <Button size="sm" variant="ghost" data-testid="survey-remove-question"
                              onClick={() => setDraft({ ...draft,
                                                        questions: draft.questions.slice(0, -1) })}>
                        Remove the last one
                      </Button>
                    )}
                  </div>
                </div>

                <Button size="sm" data-testid="survey-save-draft"
                        disabled={!draft.key || !draft.name}
                        onClick={() => post("/surveys/drafts",
                                             { key: draft.key, name: draft.name,
                                               questions: draft.questions,
                                               sample_pct: draft.sample_pct,
                                               provider_key: draft.provider_key || null },
                                             "Draft saved")}>
                  Save as draft
                </Button>
              </section>
            )}

            <section className="space-y-2 border-t pt-6" data-testid="survey-versions">
              <h2 className="text-base md:text-lg font-medium">Survey versions</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border rounded-md">
                  <caption className="sr-only">Survey configuration versions</caption>
                  <thead>
                    <tr className="text-left border-b">
                      <th scope="col" className="px-3 py-2">Key</th>
                      <th scope="col" className="px-3 py-2">Title</th>
                      <th scope="col" className="px-3 py-2">Version</th>
                      <th scope="col" className="px-3 py-2">Status</th>
                      <th scope="col" className="px-3 py-2">Questions</th>
                      <th scope="col" className="px-3 py-2">Sampling</th>
                      <th scope="col" className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bench.surveys.map((s) => (
                      <tr key={s.record_id} className="border-b last:border-0"
                          data-testid={`survey-row-${s.key}-${s.config_version}`}>
                        <th scope="row" className="px-3 py-2 font-mono text-left">{s.key}</th>
                        <td className="px-3 py-2">{s.name}</td>
                        <td className="px-3 py-2">v{s.config_version}</td>
                        <td className="px-3 py-2">{s.status}</td>
                        <td className="px-3 py-2">{s.questions.length}</td>
                        <td className="px-3 py-2">{s.trigger?.sample_pct ?? 100}%</td>
                        <td className="px-3 py-2 space-x-1">
                          <Button size="sm" variant="ghost"
                                  data-testid={`survey-preview-${s.key}-${s.config_version}`}
                                  onClick={() => apiFetch(`/surveys/drafts/${s.record_id}/preview`)
                                    .then((r) => (r.ok ? r.json() : null)).then(setPreview)}>
                            Preview
                          </Button>
                          {s.status === "draft" && bench.can_author && (
                            <Button size="sm" variant="ghost"
                                    data-testid={`survey-validate-${s.key}-${s.config_version}`}
                                    onClick={() => post(`/surveys/drafts/${s.record_id}/advance`,
                                                         { action: "validate" }, "Validated")}>
                              Validate
                            </Button>
                          )}
                          {s.status === "validated" && bench.can_publish && (
                            <Button size="sm" variant="ghost"
                                    data-testid={`survey-publish-${s.key}-${s.config_version}`}
                                    onClick={() => post(`/surveys/drafts/${s.record_id}/advance`,
                                                         { action: "publish" }, "Published")}>
                              Publish
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {bench.surveys.length === 0 && (
                      <tr><td colSpan="7" className="px-3 py-2 text-muted-foreground"
                              data-testid="survey-versions-empty">
                        No surveys yet.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {preview && (
              <section className="space-y-3 border-t pt-6" data-testid="survey-preview">
                <div className="flex items-center justify-between">
                  <h2 className="text-base md:text-lg font-medium"
                      data-testid="survey-preview-title">
                    Preview: {preview.name} (v{preview.config_version}, {preview.status})
                  </h2>
                  <Button size="sm" variant="ghost" data-testid="survey-preview-close"
                          onClick={() => setPreview(null)}>Close</Button>
                </div>
                <p className="text-sm">{preview.intro}</p>
                <ul className="space-y-3">
                  {preview.questions.map((q) => (
                    <li key={q.key} data-testid={`survey-preview-question-${q.key}`}>
                      <p className="text-sm font-medium">
                        {q.label}{q.required && <span aria-hidden="true"> *</span>}
                      </p>
                      {q.type === "rating" ? (
                        <div className="flex gap-2 mt-1">
                          {q.scale.map((n) => (
                            <span key={n}
                                  className="border rounded-md px-3 py-1 text-sm text-muted-foreground">
                              {n}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="border rounded-md px-3 py-2 text-sm text-muted-foreground mt-1">
                          free text
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground" data-testid="survey-preview-note">
                  {preview.note}
                </p>
              </section>
            )}

            {Object.keys(bench.results).length > 0 && (
              <section className="space-y-2 border-t pt-6" data-testid="survey-results">
                <h2 className="text-base md:text-lg font-medium">Results</h2>
                <ul className="text-sm space-y-1">
                  {Object.entries(bench.results).map(([key, r]) => (
                    <li key={key} data-testid={`survey-result-${key}`}>
                      <span className="font-mono">{key}</span>: {r.response_count} response(s)
                      {Object.keys(r.averages).length > 0 && (
                        <span>
                          {" "}· averages{" "}
                          {Object.entries(r.averages).map(([q, v]) => `${q} ${v}`).join(", ")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
